import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/authStore';
import { STRINGS } from '@/strings/strings';

type WorldSlug = 'aa' | 'kp' | 'sb';

const VALID_SLUGS: readonly WorldSlug[] = ['aa', 'kp', 'sb'];

function isValidSlug(value: string | undefined): value is WorldSlug {
  return typeof value === 'string' && (VALID_SLUGS as readonly string[]).includes(value);
}

function NumberedCircle({ n }: { n: number }) {
  return (
    <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-dream-gold text-dream-navy text-xs font-semibold shrink-0">
      {n}
    </span>
  );
}

const SECTION_CLASS = 'rounded-xl bg-white/5 border border-white/10 p-4';

export function HowItWorksRoute() {
  const navigate = useNavigate();
  const updateProfile = useAuthStore((s) => s.updateProfile);
  const params = useParams<{ world: string }>();
  const S = STRINGS.onboarding.howItWorks;
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Defensive: invalid slug (deep-link / typo / refresh after state-loss)
  // → redirect back to world-selection. Effect, not early-return, so
  // React's render is clean while the navigation queues.
  useEffect(() => {
    if (!isValidSlug(params.world)) {
      navigate('/onboarding/world', { replace: true });
    }
  }, [params.world, navigate]);

  if (!isValidSlug(params.world)) {
    return null;
  }
  const world: WorldSlug = params.world;

  // Sourced from STRINGS.onboarding.world (sibling-route consistency,
  // same German full names as the WorldSelectionRoute cards). No new
  // imports — STRINGS is already imported above.
  const worldNameBySlug: Record<WorldSlug, string> = {
    aa: STRINGS.onboarding.world.aaName,
    kp: STRINGS.onboarding.world.kpName,
    sb: STRINGS.onboarding.world.sbName,
  };
  const subtitle = S.subtitleTemplate.replace('{worldLabel}', worldNameBySlug[world]);

  const handleStart = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      await updateProfile({ onboardingCompleted: true });
    } catch (err) {
      // Lax: log but don't block navigation. If PATCH failed, user will
      // re-enter onboarding on next login per computePostAuthRedirect —
      // annoying but not data loss.
      console.error('[HowItWorks] failed to mark onboardingCompleted:', err);
    }
    navigate(`/builder/${world}`, { replace: true });
  };

  return (
    <>
      <h1 className="text-[22px] font-semibold text-dream-white mb-3">{S.title}</h1>
      <p className="text-sm text-dream-white/90 leading-relaxed mb-6">{subtitle}</p>

      <section data-testid="block-aa-kp" className={`${SECTION_CLASS} mb-4`}>
        <h2 className="text-base font-semibold text-dream-white mb-1.5">{S.blockAaKp.heading}</h2>
        <p className="text-xs text-dream-white/80 mb-3">{S.blockAaKp.subLine}</p>

        <div className="space-y-3">
          <div className="flex gap-3 items-start">
            <NumberedCircle n={1} />
            <div>
              <p className="text-sm font-semibold text-dream-white">{S.blockAaKp.card1.title}</p>
              <p className="text-xs text-dream-white/80 leading-relaxed mt-0.5">
                {S.blockAaKp.card1.body}
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <NumberedCircle n={2} />
            <div>
              <p className="text-sm font-semibold text-dream-white">{S.blockAaKp.card2.title}</p>
              <p className="text-xs text-dream-white/80 leading-relaxed mt-0.5">
                {S.blockAaKp.card2.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section data-testid="block-sb" className={`${SECTION_CLASS} mb-6`}>
        <h2 className="text-base font-semibold text-dream-white mb-1.5">{S.blockSb.heading}</h2>
        <p className="text-xs text-dream-white/80 mb-3">{S.blockSb.subLine}</p>

        <p className="text-sm font-semibold text-dream-white mb-2">{S.blockSb.subsectionATitle}</p>
        <div className="space-y-3 mb-4">
          <div className="flex gap-3 items-start">
            <NumberedCircle n={1} />
            <div>
              <p className="text-sm font-semibold text-dream-white">{S.blockSb.itemA1.title}</p>
              <p className="text-xs text-dream-white/80 leading-relaxed mt-0.5">
                {S.blockSb.itemA1.body}
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <NumberedCircle n={2} />
            <div>
              <p className="text-sm font-semibold text-dream-white">{S.blockSb.itemA2.title}</p>
              <p className="text-xs text-dream-white/80 leading-relaxed mt-0.5">
                {S.blockSb.itemA2.body}
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <NumberedCircle n={3} />
            <div>
              <p className="text-sm font-semibold text-dream-white">{S.blockSb.itemA3.title}</p>
              <p className="text-xs text-dream-white/80 leading-relaxed mt-0.5">
                {S.blockSb.itemA3.body}
              </p>
            </div>
          </div>
        </div>

        <p className="text-sm font-semibold text-dream-white mb-2">{S.blockSb.subsectionBTitle}</p>
        <div className="space-y-3">
          <div className="flex gap-3 items-start">
            <span className="text-xl shrink-0 leading-none" aria-hidden>
              {S.blockSb.cardB1.emoji}
            </span>
            <div>
              <p className="text-sm font-semibold text-dream-white">{S.blockSb.cardB1.title}</p>
              <p className="text-xs text-dream-white/80 leading-relaxed mt-0.5">
                {S.blockSb.cardB1.body}
              </p>
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <span className="text-xl shrink-0 leading-none" aria-hidden>
              {S.blockSb.cardB2.emoji}
            </span>
            <div>
              <p className="text-sm font-semibold text-dream-white">{S.blockSb.cardB2.title}</p>
              <p className="text-xs text-dream-white/80 leading-relaxed mt-0.5">
                {S.blockSb.cardB2.body}
              </p>
            </div>
          </div>
        </div>
      </section>

      <p className="text-xs text-dream-white/70 leading-relaxed mb-6 text-center">
        {S.inspirationHint}
      </p>

      <Button
        type="button"
        onClick={handleStart}
        disabled={isSubmitting}
        className="w-full h-12 text-base font-semibold"
      >
        {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
        {S.ctaButton}
      </Button>
    </>
  );
}
